/*
 * EntityCard.tsx - Rendern eines Entities als <dl> etc. anhand von Daten und jsonschema.
 *
 * Created by Dr. Maximillian Dornseif 2020-10-21
 * Copyright (c) 2020-2023 HUDORA GmbH
 */

import { LinkRender } from '../hd-link'
import { LadeChecker, useSchema } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { assertIsObject, assertIsString } from 'assertate-debug'
import { JSONSchema7Definition } from 'json-schema'
import React from 'react'
import { IEntity } from 'types'

const sx = { fontSize: 14 }

interface IEntityCardProps {
  entity: IEntity
  schemaName: string
  title: string
}

/** Erstellt aus einem Entity und dem JSON-Schema eine akzeptable Darstellung
 * */
export function EntityCard(props: IEntityCardProps) {
  const { loading, error, schema } = useSchema(props.schemaName)
  assertIsString(props.schemaName, 'schemaName', 'schemaName fehlt')

  if (!props.entity) {
    return null
  }

  let dlRows = []
  if (schema && schema.type === 'object') {
    // Rekursiv das Objekt und das Schema auseinander pflücken
    dlRows = objHandler(schema, props.entity, 'p_')
  }

  return (
    <div style={{ width: '100%' }}>
      <LadeChecker loading={loading || !props.schemaName} error={error} label={'Lade Schema …'} />
      <Card>
        <CardContent sx={sx}>
          <section title={`Entity ${props.schemaName}:${props.entity?.designator}`}>
            <Typography color="text.secondary" gutterBottom>
              {props.title ? (
                props.title
              ) : (
                <>
                  {schema?.title} {props.entity?.designator} {loading} {error}
                </>
              )}
            </Typography>
            <dl style={{ columnCount: 2, columnWidth: '16em' }}>{dlRows}</dl>
          </section>
        </CardContent>
      </Card>
    </div>
  )
}

/** Objekt und Schema rekursiv in HTML Table Rows wandeln.
 *
 * zB http://f.foxel.org/Screen-Shot-2021-01-24-08-03-53.97-1611471839.png
 * */
function objHandler(subSchema: JSONSchema7Definition, subEntity: Record<string, any>, prefix: string) {
  const localTable = []

  if (subSchema === true || subSchema === false || !subSchema) {
    // type guard
    return localTable
  }

  if (subSchema?.properties) {
    // Erst alle Scalar Werte
    for (const [key, property] of Object.entries(subSchema?.properties)) {
      assertIsObject(property)
      if (property.type !== 'object') {
        localTable.push(
          <EntityCardRenderAutoRow
            key={`${prefix}_${key}_${subEntity.title}`}
            schema={property}
            data={subEntity[key]}
          />
        )
      }
    }

    // Dann alle Sub-Entities (Objekte)
    for (const [key, property] of Object.entries(subSchema?.properties)) {
      if (property === true || property === false) {
        // type guard
        continue
      }
      if (property.type === 'object') {
        localTable.push(
          <dt>
            <b>{property.title}</b>
          </dt>
        )
        localTable.push(
          <dd>
            {objHandler(
              property,
              subEntity[key],
              `${prefix}-${key}-${subEntity[key]}-${property.$id}-${property.title}1`
            )}
          </dd>
        )
        //       if (subEntity[key]) {
        //         localTable.push(
        //           <tr
        //             key={`${prefix}-${key}-${subEntity[key]}-${property.$id}-${property.title}1`}
        //             style={{ marginTop: '130%', borderBottom: '1px' }}
        //           >
        //             <th colSpan={2}>
        //               <b>{property.title}</b>
        //             </th>
        //           </tr>
        //         )
        //         const v = subEntity[key]
        //         localTable = localTable.concat(objHandler(property, v, `${prefix}_${key}`, tableRows, level + 1))
        // }
      }
    }
  }
  return localTable
}

/** Einen Skalar Wert rendern */
export const EntityCardRenderAutoRow = (props: { data: any; schema: JSONSchema7Definition }) => {
  // Leere Zeilen werden komplett ignoriert
  if (props.data === undefined) {
    return null
  }

  if (props.schema === true || props.schema === false || !props.schema) {
    // type guard
    return null
  }

  let value
  if (
    (props.schema?.title?.endsWith('№') || (props.schema?.items as any)?.title?.endsWith('№')) &&
    props.data &&
    [props.data].flat().length > 0
  ) {
    // Felder, deren Titel auf № endet, werden automatisch verlinkt
    value = (
      <>
        {[props.data]
          .flat()
          .map<React.ReactNode>((x) => <LinkRender key={x} value={x} />)
          .reduce((prev, curr) => [prev, ', ', curr])}
      </>
    )
  } else {
    value = <ValueRender value={props.data} format={props.schema?.format} />
  }
  return (
    <>
      <dt title={props.schema.description}>
        <Typography sx={sx} color="text.secondary">
          {props.schema.title}
        </Typography>
      </dt>
      <dd>
        <Box sx={sx}>{value}</Box>
      </dd>
    </>
  )
}
